/**
 * TypeScript view of the `akari` format.
 * Source of truth: `shared/schema/akari.{definition,submission,solution,progress}.json`.
 */

export type Localized = Record<string, string | undefined>;

export interface AkariDefinition {
  puzzleType: "akari";
  typeVersion: number;
  rows: number;
  cols: number;
  /** One string per row: `.` a white cell, `#` a black cell, `0`–`4` a numbered black cell. */
  grid: string[];
  rulesText?: Localized;
  meta?: Record<string, unknown>;
}

export const WHITE = ".";
export const BLACK = "#";
export const NUMBERS = "01234";
export const BULB = "*";
export const EMPTY = ".";
/** A solving aid: the player is sure the cell holds no bulb. Never sent to the server. */
export const CROSS = "x";
export type Mark = typeof BULB | typeof CROSS | typeof EMPTY;

/** Autosave shape. Opaque to the core; this type owns it and its migrations. */
export interface AkariProgress {
  version: 1;
  /** One string per row, `*` a bulb, `x` a cross, `.` undecided. */
  rows: string[];
}

/** One string per row, `*` a bulb and `.` anything else: crosses are folded into `.`. */
export interface AkariSubmission {
  rows: string[];
}

/** The author's bulbs, in the submission format. */
export type AkariSolution = AkariSubmission;

export const MAX_SIZE = 30;

export type Cell = [row: number, col: number];

export function cellId(row: number, col: number): string {
  return `r${row}c${col}`;
}

/** Picks the viewer's language, falling back to the base language, then English, then any. */
export function localized(value: Localized | null | undefined, locale: string): string {
  if (!value) return "";
  return (
    value[locale] ??
    value[locale.split("-")[0]] ??
    value.en ??
    Object.values(value).find((v) => typeof v === "string" && v.length > 0) ??
    ""
  );
}
